import { NetworkAssessmentAnalysisType, RegionSetStatusType } from "@app/projects/enumerators";
import { Component } from "react";
import { AdequacyFilters } from "../../projects/network-assessment/adequacy";
import { ExportFormatType, ExportGapFilterType, MembershipType } from "../enumerations";
import { ExportAdequacySpecialtySummaryProjectModel, ExportAdequacySummariesModel, ExportAttestationSummariesModel, ExportCompositeSummariesModel, RegionSetFilter, SpecialtyFilter } from "../exportModels";
import ExportService from "../exportService";
import * as helpers from "./exportPanelBodyHelpers";
import { SelectFormat, SelectSpecialtyFilter, SelectMembershipFilter } from "./filters";
import GenerateBody from "./generate-body";
import { ReportPanelBodyBaseProps, ReportPanelBodyBaseState } from "./reportPanelBodyBase";
import * as toast from "./toast";
import { FeatureFlagContextProps, Features, withFeatureFlagContext } from "../../feature-flags";
import SpecialtySummarySelectRegionSetFilter from "./filters/SpecialtySummarySelectRegionSetFilter";

interface SpecialtySummaryByRegionPanelBodyProps extends ReportPanelBodyBaseProps, FeatureFlagContextProps {
  adequacyFilters: AdequacyFilters;
  enableMembershipTypeOptions: boolean;
  projectId: number;
  runConfigurationId: number;
  showExpandableSpecialtiesOption: boolean;
  specialtyGroupCodes: string[];
}

interface SpecialtySummaryByRegionPanelBodyState extends ReportPanelBodyBaseState {
  regionSetFilter: RegionSetFilter;
  sharedUsers: number[];
  specialtyFilter: SpecialtyFilter;
  membershipType: MembershipType;
  hasReportNameChanged: boolean;
}

export default class SpecialtySummaryByRegionPanelBody extends Component<SpecialtySummaryByRegionPanelBodyProps, SpecialtySummaryByRegionPanelBodyState> {
  private readonly exportService = new ExportService();
  private readonly testIdSuffix = "specialty-summary-by-region-panel";

  constructor(props: SpecialtySummaryByRegionPanelBodyProps) {
    super(props);

    this.state = {
      ...this.getInitialBodyState(),
    };
  }

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    return (
      <>
        <div className="row">
          <div className="col-12 col-sm-4">
            <SelectSpecialtyFilter
              adequacyAnalysisType="county"
              disabled={this.props.disableSpecialtyGroupFilters}
              onExpandableSpecialtyIncludeChange={this.handleExpandableSpecialtyIncludeChange}
              onSpecialtyGapFilterChange={this.handleSpecialtyGapFilterChange}
              onSpecialtyIncludeChange={this.handleSpecialtyIncludeChange}
              showExpandableSpecialtyOption={this.props.showExpandableSpecialtiesOption}
              specialtyFilter={this.state.specialtyFilter}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SpecialtySummarySelectRegionSetFilter
              disabled={this.props.disableRegionFilters}
              onNetworkAssessmentAnalysisTypeChange={this.handleNetworkAssessmentAnalysisTypeChange}
              onSecondaryNetworkAssessmentAnalysisTypeChange={this.handleSecondaryNetworkAssessmentAnalysisTypeChange}
              onRegionSetFilterTypeChange={this.handleRegionSetFilterTypeChange}
              onRegionSetIncludeChange={this.handleRegionSetIncludeChange}
              regionSetFilter={this.state.regionSetFilter}
              singularDisplay={this.props.singularDisplay}
              pluralDisplay={this.props.pluralDisplay}
              testIdSuffix={this.testIdSuffix}
            />
          </div>
          <div className="col-12 col-sm-4">
            <SelectFormat outputFormat={this.state.outputFormat} onOutputFormatChange={this.handleOutputFormatChange} excelDisabled={false} exportType="specialty-summary-by-region" />
            {this.props.enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT) &&
              <SelectMembershipFilter
              disabled={!this.props.enableMembershipTypeOptions}
              membershipTypeFilter={this.state.membershipType}
              onMembershipTypeChange={this.handleMembershipTypeChange}
              testIdSuffix={this.testIdSuffix}
              />
            }
          </div>
        </div>
        <GenerateBody
          {...this.props}
          emailOnSuccess={this.state.emailOnSuccess}
          reportName={this.state.reportName}
          onGenerateClick={this.handleGenerateClick}
          onEmailOnSuccessChange={this.handleEmailOnSuccessChange}
          onReportNameChange={this.handleReportNameChange}
          isLoading={this.state.loadingReportSettingsJSONStrings && this.state.loadingReportSettingsJSONStrings.indexOf(helpers.stringifyStateReportSettings(this.state)) > -1}
          isValid={this.state.regionSetFilter.includeActive || this.state.regionSetFilter.includePending || this.state.regionSetFilter.includeApproved}
          showSharedUsers={this.props.match.params.module === "adequacy"}
          sharedUsers={this.state.sharedUsers}
          updateSelectedSharedUsers={this.updateSharedUsers}
          exportType="AdequacySummary"
          projectId={this.props.projectId}
          networkAssessmentAnalysisType={this.state.regionSetFilter.networkAssessmentAnalysisType}
        />
      </>
    );
  }

  private getInitialBodyState = (): SpecialtySummaryByRegionPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      membershipType: MembershipType.Primary,
      outputFormat: ExportFormatType.Excel,
      regionSetFilter: {
        filterType: ExportGapFilterType.All,
        includeActive: true,
        includeApproved: true,
        includePending: true,
        networkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType === NetworkAssessmentAnalysisType.ServiceArea ? this.props.initialNetworkAssessmentAnalysisType : null,
        secondaryNetworkAssessmentAnalysisType: this.props.initialNetworkAssessmentAnalysisType === NetworkAssessmentAnalysisType.Opportunity ? this.props.initialNetworkAssessmentAnalysisType : null
      },
      reportName: helpers.reportName((
        !this.props.enableMembershipTypeOptions
        && this.props.comparedRunConfigurationId !== null) ? "Summary Comparison" : "Summary"),
      sharedUsers: [],
      specialtyFilter: {
        filterType: ExportGapFilterType.All,
        includeExpandableSpecialties: false,
        includeFacilitySpecialties: true,
        includePhysicianSpecialties: true,
      },
    } as SpecialtySummaryByRegionPanelBodyState;
  }

  private handleRegionSetFilterTypeChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        filterType,
      },
    }));
  }

  private handleNetworkAssessmentAnalysisTypeChange = (networkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        networkAssessmentAnalysisType,
      },
    }));
  }

  private handleSecondaryNetworkAssessmentAnalysisTypeChange = (secondaryNetworkAssessmentAnalysisType: NetworkAssessmentAnalysisType) => {
    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        secondaryNetworkAssessmentAnalysisType,
      },
      reportSettingsHasChanges: true
    }));
  }

  private handleRegionSetIncludeChange = (selected: boolean, regionSetStatus: RegionSetStatusType) => {
    let activeSelected: boolean = this.state.regionSetFilter.includeActive;
    let approvedSelected: boolean = this.state.regionSetFilter.includeApproved;
    let pendingSelected: boolean = this.state.regionSetFilter.includePending;

    switch (regionSetStatus) {
      case RegionSetStatusType.Active:
        activeSelected = selected;
        break;
      case RegionSetStatusType.Approved:
        approvedSelected = selected;
        break;
      case RegionSetStatusType.Pending:
        pendingSelected = selected;
        break;
    }

    this.setState((prevState) => ({
      ...prevState,
      regionSetFilter: {
        ...prevState.regionSetFilter,
        includeActive: activeSelected,
        includeApproved: approvedSelected,
        includePending: pendingSelected,
      }
    }));
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat });
  }

  private handleMembershipTypeChange = (membershipType: MembershipType) => {
    this.setState({
      membershipType,
        // Change the default report name based on the membership type selection if the user hasn't manually changed the report name.
        reportName: this.state.hasReportNameChanged
        ? this.state.reportName
        : helpers.reportName(membershipType == MembershipType.Both ? "Summary Comparison" : "Summary"),
    });
  }

  private handleGenerateClick = async () => {
    const stateReportSettingsJSON = helpers.stringifyStateReportSettings(this.state);
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [
        ...prevState.loadingReportSettingsJSONStrings,
        stateReportSettingsJSON,
      ],
    }));

    switch (this.props.match.params.module) {
      case "adequacy":
        await this.generateExportAdequacySummaryModel();
        break;
      case "attestation":
        await this.generateExportAttestationSummaryModel();
        break;
      case "composite":
        await this.generateExportCompositeSummaryModel();
        break;
      default:
        break;
    }

    toast.toastInfo(this.state.reportName);
  }

  private generateExportAdequacySummaryModel = async () => {
    if (this.props.enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT)) {
      const summariesModel: ExportAdequacySpecialtySummaryProjectModel = {
        changedGapsOnly: !!this.props.adequacyFilters.changedGaps,
        comparedProjectRunConfigurationId: this.props.comparedRunConfigurationId,
        customProjectId: this.props.projectId,
        eMailOnSuccess: this.state.emailOnSuccess,
        exportName: this.state.reportName,
        formatType: this.state.outputFormat,
        includeActiveRegionSets: this.state.regionSetFilter.includeActive,
        includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
        includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
        includePendingRegionSets: this.state.regionSetFilter.includePending,
        includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
        membershipType: this.props.enableMembershipTypeOptions ? this.state.membershipType : MembershipType.Primary,
        networkAssessmentAnalysisTypes: this.getRegionSetNetworkAssessmentAnalysisTypes(this.state.regionSetFilter),
        projectRunConfigurationId: this.props.runConfigurationId,
        projectURL: this.props.projectURL,
        regionSetGapFilter: this.state.regionSetFilter.filterType,
        regionSetIds: this.props.regionSetIds,
        runAsynchronous: true,
        sharedUsers: this.state.sharedUsers,
        showExpandableSpecialties: this.state.specialtyFilter.includeExpandableSpecialties,
        specialtyGapFilter: this.state.specialtyFilter.filterType,
        specialtyGroupCodes: this.props.specialtyGroupCodes,
      };

      await this.exportService.postExportAdequacySpecialtySummaries(summariesModel);
    }
    else {
      const summariesModel: ExportAdequacySummariesModel = {
        changedGapsOnly: !!this.props.adequacyFilters.changedGaps,
        comparedProjectRunConfigurationId: this.props.comparedRunConfigurationId,
        customProjectId: this.props.projectId,
        eMailOnSuccess: this.state.emailOnSuccess,
        exportName: this.state.reportName,
        formatType: this.state.outputFormat,
        includeActiveRegionSets: this.state.regionSetFilter.includeActive,
        includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
        includeExpandableSpecialties: this.state.specialtyFilter.includeExpandableSpecialties,
        includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
        includePendingRegionSets: this.state.regionSetFilter.includePending,
        includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
        networkAssessmentAnalysisType: this.state.regionSetFilter.networkAssessmentAnalysisType,
        projectRunConfigurationId: this.props.runConfigurationId,
        projectURL: this.props.projectURL,
        regionSetGapFilter: this.state.regionSetFilter.filterType,
        regionSetIds: this.props.regionSetIds,
        runAsynchronous: true,
        sharedUsers: this.state.sharedUsers,
        showCMSCredit: true,
        specialtyGapFilter: this.state.specialtyFilter.filterType,
        specialtyGroupCodes: this.props.specialtyGroupCodes,
      };

      await this.exportService.postExportAdequacySummaries(summariesModel);
    }
  }

  private generateExportAttestationSummaryModel = async () => {
    const summariesModel: ExportAttestationSummariesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };

    await this.exportService.postExportAttestationSummaries(summariesModel);
  }

  private generateExportCompositeSummaryModel = async () => {
    const summariesModel: ExportCompositeSummariesModel = {
      customProjectId: this.props.projectId,
      eMailOnSuccess: this.state.emailOnSuccess,
      exportName: this.state.reportName,
      formatType: this.state.outputFormat,
      includeActiveRegionSets: this.state.regionSetFilter.includeActive,
      includeApprovedRegionSets: this.state.regionSetFilter.includeApproved,
      includeFacilitySpecialties: this.state.specialtyFilter.includeFacilitySpecialties,
      includePendingRegionSets: this.state.regionSetFilter.includePending,
      includePhysicianSpecialties: this.state.specialtyFilter.includePhysicianSpecialties,
      projectRunConfigurationId: this.props.runConfigurationId,
      projectURL: this.props.projectURL,
      regionSetGapFilter: this.state.regionSetFilter.filterType,
      regionSetIds: this.props.regionSetIds,
      runAsynchronous: true,
      specialtyGapFilter: this.state.specialtyFilter.filterType,
      specialtyGroupCodes: this.props.specialtyGroupCodes,
    };

    await this.exportService.postExportCompositeSummaries(summariesModel);
  }

  private getRegionSetNetworkAssessmentAnalysisTypes = (regionSetFilter: RegionSetFilter): NetworkAssessmentAnalysisType[] => {
    const networkAssessmentAnalysisTypes = [];
    if (regionSetFilter.networkAssessmentAnalysisType != null)
      networkAssessmentAnalysisTypes.push(regionSetFilter.networkAssessmentAnalysisType);
    if (regionSetFilter.secondaryNetworkAssessmentAnalysisType != null)
      networkAssessmentAnalysisTypes.push(regionSetFilter.secondaryNetworkAssessmentAnalysisType);
    return networkAssessmentAnalysisTypes;
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess });
  }

  private handleReportNameChange = (reportName: string) => {
    if (!this.state.hasReportNameChanged) {
      this.setState({ hasReportNameChanged: true });
    }
    this.setState({ reportName });
  }

  private updateSharedUsers = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }

  private handleExpandableSpecialtyIncludeChange = (selected: boolean) => {
    this.setState((prevState) => ({
      ...prevState,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeExpandableSpecialties: selected
      },
    }));
  }

  private handleSpecialtyIncludeChange = (selected: boolean, isPhysician: boolean) => {

    let physiciansSelected: boolean = this.state.specialtyFilter.includePhysicianSpecialties;
    let facilitiesSelected: boolean = this.state.specialtyFilter.includeFacilitySpecialties;

    if (isPhysician) {
      physiciansSelected = selected;
    } else {
      facilitiesSelected = selected;
    }

    this.setState((prevState) => ({
      ...prevState,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        includeFacilitySpecialties: facilitiesSelected,
        includePhysicianSpecialties: physiciansSelected,
      },
    }));
  }

  private handleSpecialtyGapFilterChange = (filterType: ExportGapFilterType) => {
    this.setState((prevState) => ({
      ...prevState,
      specialtyFilter: {
        ...prevState.specialtyFilter,
        filterType,
      },
    }));
  }
}

const WrappedSpecialtySummaryByRegionPanelBody = withFeatureFlagContext(SpecialtySummaryByRegionPanelBody);

export { WrappedSpecialtySummaryByRegionPanelBody as SpecialtySummaryByRegionPanelBody }
